import { Injectable, NgZone } from '@angular/core';
import {
    errorMessages,
    getKnownAuthorities,
    localConfig,
    loginPanelHashbangTrigger
} from '@core/authentication/authentication.config';
import {
    AuthMode,
    authModeKey,
    authTokensKey,
    B2cAdConfiguration,
    Journey,
    LoginState,
    MSALErrorCode
} from '@core/authentication/authentication.models';
import { ServerEnvironments } from '../../../../server/server-environments.enum';
import { ServerTokensService } from '@core/services/server-tokens.service';
import { PlatformService } from '@core/services/platform.service';
import {
    AccountInfo,
    AuthenticationResult,
    AuthError,
    Configuration,
    InteractionRequiredAuthError,
    NavigationClient,
    PublicClientApplication,
    RedirectRequest
} from '@azure/msal-browser';
import { CustomNavigationClient } from '@core/authentication/custom-navigation-client';
import { take } from 'rxjs/operators';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { AuthenticationJourney } from '@models/api-customers.models';
import { HttpClient } from '@angular/common/http';
import { StorageService } from '@core/services/storage.service';
import { Router } from '@angular/router';
import { LoggingService } from '@core/services/logging.service';
import { NotificationService } from '@core/services/notification/notification.service';

@Injectable({
    providedIn: 'root'
})
export class MsalAuthenticationService {
    private msalInstance: PublicClientApplication;
    private readonly isLocal = this.serverTokensService.serverEnv === ServerEnvironments.Dev;
    private readonly config = this.getConfiguration();
    private readonly knownAuthorities = getKnownAuthorities(this.config);
    private loginStateRs$ = new BehaviorSubject<LoginState>({ loggedIn: false, authMode: 'AzureB2C' });

    public loginState$ = this.loginStateRs$.asObservable();

    constructor(
        private ngZone: NgZone,
        private router: Router,
        private httpClient: HttpClient,
        private loggingService: LoggingService,
        private storageService: StorageService,
        private platformService: PlatformService,
        private notificationService: NotificationService,
        private serverTokensService: ServerTokensService
    ) {
        this.loginState$.subscribe(loginState => {
            const { loggedIn, token } = loginState;
            if (loggedIn && token) {
                const account = this.getAccount();
                if (account) {
                    this.saveToken(account, token);
                }
            }
        });
    }

    public async init(mode?: AuthMode): Promise<void> {
        const authMode: AuthMode | undefined = mode ?? this.storageService.get(authModeKey);
        if (authMode !== 'AzureB2C') {
            return;
        }

        if (!this.msalInstance) {
            this.storageService.set(authModeKey, authMode);
            if (this.platformService.isBrowser) {
                await this.initializeMsalInstance();
            }
        }

        const account = this.getAccount();
        if (account) {
            const token = this.getSavedToken(account);
            this.loginStateRs$.next({
                loggedIn: true,
                authMode: 'AzureB2C',
                username: account.username,
                token
            });
            return;
        }

        this.loginStateRs$.next({ loggedIn: false, authMode: 'AzureB2C' });
    }

    /**
     * Attempts to silently obtain a token, and if it fails will request our service to determine
     * whether to redirect the user to Sign up / Sign in or to force Reset Password flows
     * @param [userEmail] Email for checking what user flow, will send to sign up if missing or invalid
     * @param [silentOnly] If true, will not redirect to sign up / sign in or reset password flows
     */
    public async login(userEmail?: string, silentOnly?: boolean): Promise<void> {
        this.getUserAuthenticationJourney(userEmail)
            .pipe(take(1))
            .subscribe(journey => {
                switch (journey) {
                    case 'Signin':
                        this.getToken(this.getSignUpSignInRequest(userEmail), silentOnly);
                        break;
                    case 'Signup':
                        this.getToken(this.getSignUpRequest(userEmail), silentOnly);
                        break;
                    case 'ResetPassword':
                        this.getToken(this.getResetPasswordRequest(userEmail as string), silentOnly);
                        break;
                    case 'Signout':
                        this.notifyAndlogout(loginPanelHashbangTrigger);
                        break;
                }
            });
    }

    /**
     * Delete cached token and logs user out
     * @param redirectUriExtension
     */
    public logout(redirectUriExtension: string = ''): Promise<void> {
        this.deleteSavedToken();
        return this.msalInstance.logoutRedirect({
            authority: `${this.config.loginAuthorityProvider}/${this.config.loginSignUpSignInFlow}`, // Uses the sign up / sign in flow to get oauth properties
            postLogoutRedirectUri: `${window.location.origin}${redirectUriExtension}`
        });
    }

    public updateToken(error?: MSALErrorCode): void {
        if (error) {
            this.loginStateRs$.error({ loggedIn: false, error: error, authMode: 'AzureB2C' });
            return;
        }

        const account = this.getAccount();
        if (account) {
            this.loginStateRs$.next({
                loggedIn: true,
                authMode: 'AzureB2C',
                username: account.username,
                token: account.idToken
            });
        }
    }

    public getCurrentToken(): string | undefined {
        const account = this.getAccount();
        if (account) {
            return this.getSavedToken(account);
        }
    }

    private notifyAndlogout(redirectUriExtension: string = ''): void {
        this.notificationService.createNotification({
            onClose: () => this.logout(redirectUriExtension),
            type: 'info',
            fadeTime: 10000,
            translationText: 'message.signInPanelPageReloadNeeded'
        });

        this.loggingService.logMessage('Login-panel', 'User tried to sign in with new account');
    }

    private getUserAuthenticationJourney(userEmail?: string): Observable<Journey> {
        if (!userEmail) {
            return of('Signup');
        }

        const currentAccount = this.getAccount();

        if (currentAccount && currentAccount.username !== userEmail) {
            return of('Signout');
        }

        return this.httpClient.get<AuthenticationJourney>(
            `customers-ms/authentication/${encodeURIComponent(userEmail)}/journey`
        );
    }

    private async initializeMsalInstance(): Promise<void> {
        return this.ngZone.runOutsideAngular(async () => {
            this.msalInstance = new PublicClientApplication(
                this.getMsalConfig(
                    this.platformService.isIE,
                    new CustomNavigationClient(this.router, this.storageService, this.ngZone)
                )
            );

            await this.msalInstance.initialize();
            await this.msalInstance
                .handleRedirectPromise()
                .then(redirectResponse => this.handleRedirectPromise(redirectResponse))
                .catch(error => {
                    this.ngZone.run(() => {
                        this.loginStateRs$.next({ authMode: 'AzureB2C', loggedIn: false });
                        this.loggingService.logMessage(
                            'Authentication unexpected handleRedirectPromise rejection',
                            error.message
                        );
                    });
                });
        });
    }

    private disableUserMigrationRequired(): void {
        this.httpClient.post<void>(`customers-ms/user/disablerequiremigration`, undefined).toPromise();
    }

    private getMsalConfig(isIE: boolean, navigationClient: NavigationClient): Configuration {
        if (!this.config.loginClientId) {
            throw Error(errorMessages.missingClientId);
        }

        return {
            auth: {
                clientId: this.config.loginClientId,
                redirectUri: `${window.location.origin}/${this.config.loginRedirectPath}`,
                knownAuthorities: this.knownAuthorities
            },
            cache: {
                cacheLocation: 'localStorage',
                storeAuthStateInCookie: isIE
            },
            system: {
                navigationClient
            }
        } as Configuration;
    }

    private getAccount(): AccountInfo | null {
        const activeAccount = this.msalInstance?.getActiveAccount();
        if (!activeAccount) {
            const firstAccount = this.msalInstance?.getAllAccounts()?.[0];
            if (firstAccount) {
                this.msalInstance?.setActiveAccount(firstAccount);
            }
            return firstAccount;
        }
        return activeAccount;
    }

    private async getToken(
        request: RedirectRequest = this.getSignUpSignInRequest(),
        silentOnly: boolean = false
    ): Promise<string | void> {
        const account = this.getAccount();

        if (account) {
            try {
                return await this.getTokenSilent(account);
            } catch (error) {
                if (error instanceof InteractionRequiredAuthError || error instanceof AuthError) {
                    if (!silentOnly) {
                        // fallback to interaction when silent call fails
                        return await this.msalInstance.acquireTokenRedirect(request);
                    } else {
                        this.loginStateRs$.next({ loggedIn: false, authMode: 'AzureB2C' });
                    }
                } else if (error instanceof Error) {
                    this.loggingService.logMessage(
                        'Authentication acquireTokenRedirect unexpected error',
                        error.message
                    );
                } else {
                    this.loggingService.logMessage(
                        'Authentication acquireTokenRedirect unexpected error',
                        String(error)
                    );
                }
            }
        } else {
            return await this.msalInstance.acquireTokenRedirect(request);
        }
    }

    private async handleRedirectPromise(redirectResponse: AuthenticationResult | null): Promise<void> {
        if (redirectResponse) {
            if (!redirectResponse.account) {
                this.logout();
                this.loggingService.logMessage('Authentication Error', 'Account missing from B2C response');
                return;
            }
            const userFlow = this.getUserFlow(redirectResponse.idTokenClaims);
            if (userFlow === this.config.loginResetPasswordFlow) {
                this.disableUserMigrationRequired();
            }
            this.loginStateRs$.next({
                authMode: 'AzureB2C',
                loggedIn: true,
                username: redirectResponse.account?.username,
                token: redirectResponse.idToken
            });
        } else {
            const account = this.getAccount();
            if (!account) {
                this.loginStateRs$.next({ loggedIn: false, authMode: 'AzureB2C' });
            } else {
                const savedToken = this.getSavedToken(account);
                this.loginStateRs$.next({
                    authMode: 'AzureB2C',
                    loggedIn: true,
                    username: account.username,
                    token: savedToken
                });
            }
        }
    }

    private saveToken(account: AccountInfo, token: string) {
        if (this.storageService.isStorageSupported) {
            this.storageService.set(authModeKey, 'AzureB2C');
            this.storageService.set(authTokensKey, { [account.localAccountId]: token });
        }
    }

    private getSavedToken(account: AccountInfo): string | undefined {
        if (this.storageService.isStorageSupported) {
            return this.storageService.get(authTokensKey)?.[account.localAccountId];
        }
    }

    private deleteSavedToken(): void {
        // let be sure we will remove the information about the membership after logout
        this.storageService.remove('membership-data');
        this.storageService.remove(authModeKey);
        this.storageService.remove(authTokensKey);
    }

    private getUserFlow(idTokenClaims: { [key: string]: any }): string {
        const { tfp } = idTokenClaims;
        if (!tfp) {
            const error = Error('Property tfp missing from idTokenClaims');
            this.loggingService.logMessage('Authentication TFP Error', error.message);
            throw error;
        }
        return idTokenClaims.tfp;
    }

    private getSelectedRedirectRequest(): RedirectRequest {
        // for some reason when we do the silent refresh the user flow is not always available in the idTokenClaims, so we fallback to the tenantId being the tfp
        const userFlow = this.getUserFlow(
            this.getAccount()?.idTokenClaims || { tfp: this.getAccount()?.tenantId } || {}
        );
        return {
            authority: `${this.config.loginAuthorityProvider}/${userFlow}`,
            scopes: []
        };
    }

    private getSignUpSignInRequest(userEmail?: string): RedirectRequest {
        const redirectRequest: RedirectRequest = {
            authority: `${this.config.loginAuthorityProvider}/${this.config.loginSignUpSignInFlow}`,
            scopes: []
        };
        if (userEmail) {
            redirectRequest.loginHint = userEmail;
        }
        return redirectRequest;
    }

    private getSignUpRequest(userEmail?: string): RedirectRequest {
        const redirectRequest: RedirectRequest = {
            authority: `${this.config.loginAuthorityProvider}/${this.config.loginSignUpFlow}`,
            scopes: []
        };
        if (userEmail) {
            redirectRequest.loginHint = userEmail;
        }
        return redirectRequest;
    }

    private getResetPasswordRequest(userEmail: string): RedirectRequest {
        return {
            authority: `${this.config.loginAuthorityProvider}/${this.config.loginResetPasswordFlow}`,
            extraQueryParameters: { userEmail: encodeURIComponent(userEmail) },
            scopes: []
        };
    }

    private async getTokenSilent(account: AccountInfo): Promise<string | undefined> {
        const selectedRequest = this.getSelectedRedirectRequest();
        const silentTokenResponse = await this.msalInstance.acquireTokenSilent({
            ...selectedRequest,
            account,
            forceRefresh: true
        });
        const { idToken } = silentTokenResponse;
        this.loginStateRs$.next({
            authMode: 'AzureB2C',
            loggedIn: true,
            username: account.username,
            token: idToken
        });
        return idToken;
    }

    private getConfiguration(): B2cAdConfiguration {
        if (this.isLocal) {
            return localConfig;
        }

        const {
            loginRedirectPath,
            loginClientId,
            loginAuthorityProvider,
            loginSignUpSignInFlow,
            loginSignUpFlow,
            loginResetPasswordFlow
        } = this.serverTokensService;

        return {
            loginRedirectPath,
            loginClientId,
            loginAuthorityProvider,
            loginSignUpSignInFlow,
            loginSignUpFlow,
            loginResetPasswordFlow
        };
    }
}
